import { Controller } from "@hotwired/stimulus"

// Drag-to-place and resize the cover title plate in the editor preview. One
// Pointer Events path covers mouse + touch. Values are written as a % of the
// preview area into hidden fields (cookbook[cover_presentation][x|y|width]) and
// applied live via the --cover-* custom properties (CSP-safe, no inline style in
// ERB). Per javascript.md: the initial pointerdown is wired with data-action;
// transient move/up listeners are added on drag start and removed on release.
export default class extends Controller {
  static targets = ["area", "plate", "x", "y", "width", "height"]

  connect() {
    this.applyVar("x")
    this.applyVar("y")
    this.applyVar("width")
    // height 0 = auto; only pin it once the user has dragged the plate taller.
    if (Number(this.heightTarget.value) > 0) this.applyVar("height")
  }

  disconnect() {
    this.stopTracking()
  }

  startDrag(event) {
    event.preventDefault()
    this.mode = "move"
    this.track(event)
  }

  startResize(event) {
    event.preventDefault()
    event.stopPropagation() // don't also start a move-drag on the plate
    this.mode = "resize"
    this.track(event)
  }

  track(event) {
    this.plateTarget.setPointerCapture(event.pointerId)
    this.onMove = this.move.bind(this)
    this.onUp = this.stopTracking.bind(this)
    this.plateTarget.addEventListener("pointermove", this.onMove)
    this.plateTarget.addEventListener("pointerup", this.onUp)
    this.plateTarget.addEventListener("pointercancel", this.onUp)
  }

  move(event) {
    const rect = this.areaTarget.getBoundingClientRect()
    const px = ((event.clientX - rect.left) / rect.width) * 100
    const py = ((event.clientY - rect.top) / rect.height) * 100
    if (this.mode === "resize") {
      // Plate is centered on x (width = twice the gap from center to the pointer) and
      // top-anchored on y (height = the gap from the top edge down to the pointer).
      this.setVar("width", clamp(Math.abs(px - Number(this.xTarget.value)) * 2, 10, 100))
      this.setVar("height", clamp(py - Number(this.yTarget.value), 8, 100))
    } else {
      this.setVar("x", clamp(px, 0, 100))
      this.setVar("y", clamp(py, 0, 100))
    }
  }

  stopTracking() {
    if (!this.onMove) return
    this.plateTarget.removeEventListener("pointermove", this.onMove)
    this.plateTarget.removeEventListener("pointerup", this.onUp)
    this.plateTarget.removeEventListener("pointercancel", this.onUp)
    this.onMove = null
  }

  setVar(name, value) {
    const rounded = Math.round(value * 10) / 10
    this[`${name}Target`].value = rounded
    this.plateTarget.style.setProperty(`--cover-${name}`, `${rounded}%`)
  }

  applyVar(name) {
    this.plateTarget.style.setProperty(`--cover-${name}`, `${this[`${name}Target`].value}%`)
  }
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value))
}
